import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Avatar, Chip, IconButton, Tooltip, TextField,
  InputAdornment, CircularProgress, Fade, TablePagination,
  Grid, Card, CardContent, Accordion, AccordionSummary, AccordionDetails,
  useTheme, useMediaQuery, Button, Stack, Divider, Collapse,
  SwipeableDrawer, Fab, CardHeader, CardActions, Pagination
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SportsIcon from '@mui/icons-material/Sports';
import GroupIcon from '@mui/icons-material/Group';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ScaleIcon from '@mui/icons-material/Scale';
import HeightIcon from '@mui/icons-material/Height';
import AccessibilityNewIcon from '@mui/icons-material/AccessibilityNew';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import api from '../api/axiosConfig';
import { useNavigate } from 'react-router-dom';
import MediaTechnica from './MediaTechnica';
import { getMediaBaseUrl } from '../utils/mediaUrl';
import { usePositions } from '../utils/positions';

const getShortNationality = (nat) => {
  if (!nat) return '';
  // Si la nationalité contient déjà des parenthèses, extraire le code
  const match = nat.match(/\((.*?)\)/);
  if (match) return match[1];
  // Sinon, retourner les 3 premières lettres en majuscules
  return nat.slice(0, 3).toUpperCase();
};

// Styled Components
const StyledAccordion = styled(Accordion)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: '12px !important',
  boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
  '&:before': {
    display: 'none',
  },
  '&.Mui-expanded': {
    margin: theme.spacing(2, 0),
  },
  [theme.breakpoints.down('sm')]: {
    borderRadius: '8px !important',
    marginBottom: theme.spacing(1),
  },
}));

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: '12px',
  minHeight: '80px',
  '&.Mui-expanded': {
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    minHeight: '80px',
  },
  '& .MuiAccordionSummary-content': {
    margin: '16px 0',
    alignItems: 'center',
  },
}));

const PlayerChip = styled(Chip)(({ theme }) => ({
  fontWeight: 'bold',
  fontSize: '1.1rem',
  borderRadius: '20px',
  padding: '8px 12px',
  height: 'auto',
  '& .MuiChip-avatar': {
    width: 48,
    height: 48,
    marginLeft: '-12px',
    marginRight: '8px',
  },
  '& .MuiChip-label': {
    paddingLeft: 0,
    paddingRight: 4,
  }
}));

const FilterCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  borderRadius: '12px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(2),
    borderRadius: '8px',
    '& .MuiCardContent-root': {
      padding: theme.spacing(1.5),
    },
  },
}));

const StatsChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
  fontWeight: 500,
}));

const SectionCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: '8px',
  boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(1.5),
    borderRadius: '6px',
    '& .MuiCardContent-root': {
      padding: theme.spacing(2),
      '&:last-child': {
        paddingBottom: theme.spacing(2),
      },
    },
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontWeight: 'bold',
  marginBottom: theme.spacing(1),
}));

// Mobile Card Component
const MobileTechniqueCard = ({ tech, open, onToggle, navigate, t, translateFromDB }) => {
  const theme = useTheme();
  const decimalAge = calculateAge(tech.player_date_naissance, tech.fecha);
  const birthYear = tech.player_date_naissance ? new Date(tech.player_date_naissance).getFullYear() : null;
  const shortNat = getShortNationality(tech.player_nationalite);

  return (
    <Card sx={{ mb: 2, boxShadow: theme.shadows[2] }}>
      <CardHeader
        avatar={
          <Avatar src={getPlayerAvatarUrl(tech.player_id)} alt={tech.player_nom} />
        }
        title={
          <Typography 
            onClick={() => navigate(`/players/${tech.player_id}`)}
            sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' }}}
          >
            {tech.player_nom}
          </Typography>
        }
        subheader={new Date(tech.fecha).toLocaleDateString()}
        action={
          <IconButton onClick={() => onToggle(tech.id)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        }
      />
      <Collapse in={open} timeout="auto" unmountOnExit>
        <CardContent>
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar src={getAvatarUrl(tech.usuario_id)} sx={{ width: 24, height: 24 }} />
            <Typography variant="body2" color="text.secondary">
              {tech.usuario_nombre}
            </Typography>
          </Box>

          <Typography variant="subtitle1" gutterBottom>
            <strong>{t('allTechniques.position')}:</strong> {translateFromDB(tech.player_poste) || 'N/A'}
          </Typography>
          <Typography variant="subtitle1" gutterBottom>
            <strong>{t('allTechniques.age')}:</strong> {birthYear && decimalAge ? `${birthYear} (${decimalAge})` : 'N/A'}
          </Typography>
          <Typography variant="subtitle1" gutterBottom>
            <strong>{t('allTechniques.nationality')}:</strong> {shortNat || 'N/A'}
          </Typography>
          <Box sx={{ mb: 1 }}>
            <Typography variant="subtitle1" component="span">
              <strong>{t('allTechniques.team')}: </strong>
            </Typography>
            {/* Si on a des données dans competitions_detail, les afficher en priorité */}
            {tech.player_competitions_detail && tech.player_competitions_detail.length > 0 ? (
              <Box sx={{ display: 'inline-flex', flexDirection: 'column', ml: 1 }}>
                {/* Grouper par équipe */}
                {[...new Set(tech.player_competitions_detail.map(c => c.equipe_nom))].map((equipe, idx) => {
                  // Récupérer l'ID de l'équipe et les compétitions associées
                  const equipeData = tech.player_competitions_detail.find(c => c.equipe_nom === equipe);
                  const competitions = tech.player_competitions_detail
                    .filter(c => c.equipe_nom === equipe)
                    .filter((value, index, self) =>
                      index === self.findIndex((c) => c.competition_id === value.competition_id)
                    );
                  const mediaUrl = getMediaBaseUrl();
                  const timestamp = Date.now();

                  return (
                    <Box key={idx} sx={{ mb: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {/* Logo de l'équipe */}
                        <Box
                          component="img"
                          src={`${mediaUrl}/equipes/${equipeData.equipe_id}.jpg?t=${timestamp}`}
                          alt={equipe}
                          sx={{
                            width: 20,
                            height: 20,
                            objectFit: 'contain',
                            borderRadius: '2px'
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {equipe}
                        </Typography>
                      </Box>
                      {/* Logos et noms des compétitions */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', ml: 3 }}>
                        {competitions.map((comp, compIdx) => (
                          <Box key={compIdx} sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                            <Box
                              component="img"
                              src={`${mediaUrl}/competitions/${comp.competition_id}.jpg?t=${timestamp}`}
                              alt={comp.competition_nom}
                              sx={{
                                width: 16,
                                height: 16,
                                objectFit: 'contain',
                                borderRadius: '2px'
                              }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                            <Typography variant="caption">{comp.competition_nom}</Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            ) : (
              // Sinon afficher l'ancien système
              <Typography variant="body2" component="span">
                {tech.player_team || 'N/A'}
                {(tech.player_league_1 || tech.player_league_2) && (
                  <span style={{ fontSize: '12px', color: '#666' }}>
                    {' '}({[tech.player_league_1, tech.player_league_2].filter(Boolean).join(' - ')})
                  </span>
                )}
              </Typography>
            )}
          </Box>
          {tech.altura_sin_zapatos && (
            <Typography variant="subtitle1" gutterBottom>
              <strong>{t('allTechniques.height')}:</strong> {tech.altura_sin_zapatos} cm
            </Typography>
          )}
          {tech.peso_kg && (
            <Typography variant="subtitle1" gutterBottom>
              <strong>{t('allTechniques.weight')}:</strong> {tech.peso_kg} kg
            </Typography>
          )}

          {tech.player_groupes && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                <strong>{t('allTechniques.groups')}:</strong>
              </Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap">
                {parseGroups(tech.player_groupes).map((group) => (
                  <Chip
                    key={group.id}
                    label={group.libelle}
                    size="small"
                    sx={{
                      backgroundColor: group.code_couleur,
                      color: group.code_couleur === '#FFFFFF' ? 'text.primary' : 'white',
                      fontWeight: 500,
                      border: group.code_couleur === '#FFFFFF' ? '1px solid rgba(0,0,0,0.23)' : 'none',
                    }}
                  />
                ))}
              </Stack>
            </Box>
          )}

          <Divider sx={{ my: 2 }} />
          {renderFichaContent(tech)}
        </CardContent>
      </Collapse>
    </Card>
  );
};

// Helper functions
const getInitials = (prenom, nom) => {
  const firstInitial = prenom ? prenom[0] : '';
  const secondInitial = nom ? nom[0] : '';
  return (firstInitial + secondInitial).toUpperCase();
};

const getAvatarColor = (name) => {
  if (!name) return '#757575';
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash % 360);
  return `hsl(${hue}, 70%, 50%)`;
};

const getAvatarUrl = (userId) => {
  return `${getMediaBaseUrl()}/users/${userId}.png?${new Date().getTime()}`;
};

const getPlayerAvatarUrl = (playerId) => {
  return `${getMediaBaseUrl()}/photos/${playerId}.png?${new Date().getTime()}`;
};

const calculateAge = (dateNaissance, dateFiche) => {
  if (!dateNaissance || !dateFiche) return '';
  const birth = new Date(dateNaissance);
  const fiche = new Date(dateFiche);
  
  if (isNaN(birth.getTime()) || isNaN(fiche.getTime())) {
    return '';
  }
  
  const diffMs = fiche - birth;
  const ageInYears = diffMs / (1000 * 60 * 60 * 24 * 365.25);
  
  return ageInYears.toFixed(1);
};

const parseGroups = (groupsString) => {
  if (!groupsString) return [];
  return groupsString.split('|').map(group => {
    const [id, libelle, code_couleur] = group.split(':');
    return { id, libelle, code_couleur };
  });
};

const renderFieldValue = (value) => {
  if (!value || value === '' || value === '...') return '-';
  
  return (
    <Box 
      dangerouslySetInnerHTML={{ __html: value }}
      sx={{
        '& ul': {
          paddingLeft: 2,
          marginTop: 0.5,
          marginBottom: 0.5,
        },
        '& li': {
          marginBottom: 0.5,
        },
        '& p': {
          marginTop: 0.5,
          marginBottom: 0.5,
        },
      }}
    />
  );
};

const renderFichaContent = (tech) => (
  <Box>
    {/* Medidas Físicas */}
    {(tech.peso_kg || tech.body_fat_porcentaje || tech.altura_sin_zapatos || tech.altura_con_zapatos || 
      tech.entrega_vertical || tech.entrega_horizontal || tech.comentarios_observaciones_medidas || 
      tech.escala_perfil_atletico || tech.informe_medico) && (
      <SectionCard variant="outlined">
        <CardContent>
          <SectionTitle variant="h6">Medidas Físicas</SectionTitle>
          <Grid container spacing={2}>
            {tech.peso_kg && (
              <Grid item xs={6} sm={4} md={3}>
                <Typography variant="caption" color="text.secondary">Peso</Typography>
                <Typography variant="body2">{tech.peso_kg} kg</Typography>
              </Grid>
            )}
            {tech.body_fat_porcentaje && tech.body_fat_porcentaje !== "0.00" && (
              <Grid item xs={6} sm={4} md={3}>
                <Typography variant="caption" color="text.secondary">Body Fat</Typography>
                <Typography variant="body2">{tech.body_fat_porcentaje}%</Typography>
              </Grid>
            )}
            {tech.altura_sin_zapatos && (
              <Grid item xs={6} sm={4} md={3}>
                <Typography variant="caption" color="text.secondary">Altura s/ Zapatos</Typography>
                <Typography variant="body2">{tech.altura_sin_zapatos} cm</Typography>
              </Grid>
            )}
            {tech.altura_con_zapatos && tech.altura_con_zapatos !== "0.00" && (
              <Grid item xs={6} sm={4} md={3}>
                <Typography variant="caption" color="text.secondary">Altura c/ Zapatos</Typography>
                <Typography variant="body2">{tech.altura_con_zapatos} cm</Typography>
              </Grid>
            )}
            {tech.entrega_vertical && tech.entrega_vertical !== "0.00" && (
              <Grid item xs={6} sm={4} md={3}>
                <Typography variant="caption" color="text.secondary">Envergadura Vertical</Typography>
                <Typography variant="body2">{tech.entrega_vertical} cm</Typography>
              </Grid>
            )}
            {tech.entrega_horizontal && tech.entrega_horizontal !== "0.00" && (
              <Grid item xs={6} sm={4} md={3}>
                <Typography variant="caption" color="text.secondary">Envergadura Horizontal</Typography>
                <Typography variant="body2">{tech.entrega_horizontal} cm</Typography>
              </Grid>
            )}
          </Grid>
          
          {tech.comentarios_observaciones_medidas && tech.comentarios_observaciones_medidas !== '...' && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary">Comentarios y Observaciones</Typography>
              {renderFieldValue(tech.comentarios_observaciones_medidas)}
            </Box>
          )}
          {tech.escala_perfil_atletico && tech.escala_perfil_atletico !== '...' && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary">Escala Perfil Atlético</Typography>
              {renderFieldValue(tech.escala_perfil_atletico)}
            </Box>
          )}
          {tech.informe_medico && tech.informe_medico !== '...' && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary">Informe Medical</Typography>
              {renderFieldValue(tech.informe_medico)}
            </Box>
          )}
        </CardContent>
      </SectionCard>
    )}

    {/* Ataque */}
    {(tech.ataque_asp_ind_puntos_fuertes || tech.ataque_asp_ind_objetivos_mejora || 
      tech.ataque_asp_ind_observaciones_individuales || tech.ataque_asp_col_puntos_fuertes || 
      tech.ataque_asp_col_objetivos_mejora || tech.ataque_asp_col_observaciones_colectivas) && (
      <SectionCard variant="outlined">
        <CardContent>
          <SectionTitle variant="h6">Ataque</SectionTitle>
          
          {(tech.ataque_asp_ind_puntos_fuertes || tech.ataque_asp_ind_objetivos_mejora || 
            tech.ataque_asp_ind_observaciones_individuales) && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                Aspectos Individuales
              </Typography>
              {tech.ataque_asp_ind_puntos_fuertes && tech.ataque_asp_ind_puntos_fuertes !== '...' && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary">Puntos Fuertes</Typography>
                  {renderFieldValue(tech.ataque_asp_ind_puntos_fuertes)}
                </Box>
              )}
              {tech.ataque_asp_ind_objetivos_mejora && tech.ataque_asp_ind_objetivos_mejora !== '...' && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary">Objetivos de Mejora</Typography>
                  {renderFieldValue(tech.ataque_asp_ind_objetivos_mejora)}
                </Box>
              )}
              {tech.ataque_asp_ind_observaciones_individuales && tech.ataque_asp_ind_observaciones_individuales !== '...' && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Observaciones Individuales</Typography>
                  {renderFieldValue(tech.ataque_asp_ind_observaciones_individuales)}
                </Box>
              )}
            </Box>
          )}

          {(tech.ataque_asp_col_puntos_fuertes || tech.ataque_asp_col_objetivos_mejora || 
            tech.ataque_asp_col_observaciones_colectivas) && (
            <Box>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                Aspectos Colectivos
              </Typography>
              {tech.ataque_asp_col_puntos_fuertes && tech.ataque_asp_col_puntos_fuertes !== '...' && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary">Puntos Fuertes</Typography>
                  {renderFieldValue(tech.ataque_asp_col_puntos_fuertes)}
                </Box>
              )}
              {tech.ataque_asp_col_objetivos_mejora && tech.ataque_asp_col_objetivos_mejora !== '...' && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary">Objetivos de Mejora</Typography>
                  {renderFieldValue(tech.ataque_asp_col_objetivos_mejora)}
                </Box>
              )}
              {tech.ataque_asp_col_observaciones_colectivas && tech.ataque_asp_col_observaciones_colectivas !== '...' && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Observaciones Colectivas</Typography>
                  {renderFieldValue(tech.ataque_asp_col_observaciones_colectivas)}
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </SectionCard>
    )}

    {/* Defensa */}
    {(tech.defensa_asp_ind_puntos_fuertes || tech.defensa_asp_ind_objetivos_mejora || 
      tech.defensa_asp_ind_observaciones_individuales || tech.defensa_asp_col_puntos_fuertes || 
      tech.defensa_asp_col_objetivos_mejora || tech.defensa_asp_col_observaciones_colectivas) && (
      <SectionCard variant="outlined">
        <CardContent>
          <SectionTitle variant="h6">Defensa</SectionTitle>
          
          {(tech.defensa_asp_ind_puntos_fuertes || tech.defensa_asp_ind_objetivos_mejora || 
            tech.defensa_asp_ind_observaciones_individuales) && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                Aspectos Individuales
              </Typography>
              {tech.defensa_asp_ind_puntos_fuertes && tech.defensa_asp_ind_puntos_fuertes !== '...' && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary">Puntos Fuertes</Typography>
                  {renderFieldValue(tech.defensa_asp_ind_puntos_fuertes)}
                </Box>
              )}
              {tech.defensa_asp_ind_objetivos_mejora && tech.defensa_asp_ind_objetivos_mejora !== '...' && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary">Objetivos de Mejora</Typography>
                  {renderFieldValue(tech.defensa_asp_ind_objetivos_mejora)}
                </Box>
              )}
              {tech.defensa_asp_ind_observaciones_individuales && tech.defensa_asp_ind_observaciones_individuales !== '...' && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Observaciones Individuales</Typography>
                  {renderFieldValue(tech.defensa_asp_ind_observaciones_individuales)}
                </Box>
              )}
            </Box>
          )}

          {(tech.defensa_asp_col_puntos_fuertes || tech.defensa_asp_col_objetivos_mejora || 
            tech.defensa_asp_col_observaciones_colectivas) && (
            <Box>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                Aspectos Colectivos
              </Typography>
              {tech.defensa_asp_col_puntos_fuertes && tech.defensa_asp_col_puntos_fuertes !== '...' && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary">Puntos Fuertes</Typography>
                  {renderFieldValue(tech.defensa_asp_col_puntos_fuertes)}
                </Box>
              )}
              {tech.defensa_asp_col_objetivos_mejora && tech.defensa_asp_col_objetivos_mejora !== '...' && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary">Objetivos de Mejora</Typography>
                  {renderFieldValue(tech.defensa_asp_col_objetivos_mejora)}
                </Box>
              )}
              {tech.defensa_asp_col_observaciones_colectivas && tech.defensa_asp_col_observaciones_colectivas !== '...' && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Observaciones Colectivas</Typography>
                  {renderFieldValue(tech.defensa_asp_col_observaciones_colectivas)}
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </SectionCard>
    )}

    {/* Aspectos Físicos */}
    {(tech.asp_fisicos_puntos_fuertes || tech.asp_fisicos_aspectos_potenciar || 
      tech.asp_fisicos_desarrollo_motriz || tech.asp_fisicos_observaciones) && (
      <SectionCard variant="outlined">
        <CardContent>
          <SectionTitle variant="h6">Aspectos Físicos</SectionTitle>
          <Grid container spacing={2}>
            {tech.asp_fisicos_puntos_fuertes && tech.asp_fisicos_puntos_fuertes !== '...' && (
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Puntos Fuertes</Typography>
                {renderFieldValue(tech.asp_fisicos_puntos_fuertes)}
              </Grid>
            )}
            {tech.asp_fisicos_aspectos_potenciar && tech.asp_fisicos_aspectos_potenciar !== '...' && (
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Aspectos que Potenciar</Typography>
                {renderFieldValue(tech.asp_fisicos_aspectos_potenciar)}
              </Grid>
            )}
            {tech.asp_fisicos_desarrollo_motriz && tech.asp_fisicos_desarrollo_motriz !== '...' && (
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Desarrollo Motriz</Typography>
                {renderFieldValue(tech.asp_fisicos_desarrollo_motriz)}
              </Grid>
            )}
            {tech.asp_fisicos_observaciones && tech.asp_fisicos_observaciones !== '...' && (
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Observaciones</Typography>
                {renderFieldValue(tech.asp_fisicos_observaciones)}
              </Grid>
            )}
          </Grid>
        </CardContent>
      </SectionCard>
    )}

    {/* Aspectos Psicológicos */}
    {tech.asp_psicologicos_observaciones && tech.asp_psicologicos_observaciones !== '...' && (
      <SectionCard variant="outlined">
        <CardContent>
          <SectionTitle variant="h6">Aspectos Psicológicos</SectionTitle>
          {renderFieldValue(tech.asp_psicologicos_observaciones)}
        </CardContent>
      </SectionCard>
    )}

    {/* Ámbito Académico */}
    {tech.ambito_academico_observaciones && tech.ambito_academico_observaciones !== '...' && (
      <SectionCard variant="outlined">
        <CardContent>
          <SectionTitle variant="h6">Ámbito Académico</SectionTitle>
          {renderFieldValue(tech.ambito_academico_observaciones)}
        </CardContent>
      </SectionCard>
    )}

    {/* INTEL */}
    {tech.intel_observaciones && tech.intel_observaciones !== '...' && (
      <SectionCard variant="outlined">
        <CardContent>
          <SectionTitle variant="h6">INTEL</SectionTitle>
          {renderFieldValue(tech.intel_observaciones)}
        </CardContent>
      </SectionCard>
    )}

    {/* Información Audiovisual */}
    {(tech.info_audiovisual_observaciones && tech.info_audiovisual_observaciones !== '...') && (
      <SectionCard variant="outlined">
        <CardContent>
          <SectionTitle variant="h6">Información Audiovisual</SectionTitle>
          {tech.info_audiovisual_observaciones && renderFieldValue(tech.info_audiovisual_observaciones)}
          
          {/* Componente MediaTechnica para mostrar los medios */}
          <Box sx={{ mt: 2 }}>
            <MediaTechnica itemId={tech.id} />
          </Box>
        </CardContent>
      </SectionCard>
    )}
  </Box>
);

const AllTechniquesTable = () => {
  const { t } = useTranslation();
  const { translateFromDB } = usePositions(t);
  const [techniques, setTechniques] = useState([]);
  const [filteredTechniques, setFilteredTechniques] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [openRows, setOpenRows] = useState({});

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const section = process.env.REACT_APP_SECTION;

  useEffect(() => {
    fetchAllTechniques();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [techniques, searchTerm]);

  const fetchAllTechniques = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/technique/all');
      setTechniques(response.data);
      setFilteredTechniques(response.data);
    } catch (error) {
      console.error("Error al recuperar las fichas técnicas:", error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = techniques;

    if (searchTerm) {
      filtered = filtered.filter(tech => {
        const searchLower = searchTerm.toLowerCase();
        const groups = parseGroups(tech.player_groupes);
        const groupMatch = groups.some(group => 
          group.libelle.toLowerCase().includes(searchLower)
        );
        
        return tech.player_nom.toLowerCase().includes(searchLower) ||
               tech.usuario_nombre.toLowerCase().includes(searchLower) ||
               groupMatch;
      });
    }

    setFilteredTechniques(filtered);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangeMobilePage = (event, newValue) => {
    setPage(newValue - 1);
  };

  const handleToggleRow = (rowId) => {
    setOpenRows(prev => ({
      ...prev,
      [rowId]: !prev[rowId]
    }));
  };

  const navigateToPlayer = (playerId) => {
    navigate(`/players/${playerId}`);
  };

  const totalResults = filteredTechniques.length;
  const paginatedData = React.useMemo(() => {
    if (rowsPerPage <= 0) {
      return filteredTechniques;
    }
    return filteredTechniques.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredTechniques, page, rowsPerPage]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', pb: isMobile ? 7 : 0 }}>
      {isMobile ? (
        <>
          {/* Mobile View */}
          <Box
            sx={{
              mx: 2,
              mt: 2,
              mb: 1,
              display: 'flex',
              alignItems: 'center',
              p: 2,
              bgcolor: theme.palette.primary.main,
              color: 'white',
              borderRadius: 1,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}
          >
            <Box
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                borderRadius: '50%',
                width: 40,
                height: 40,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 2,
                fontWeight: 'bold',
                fontSize: '1.1rem',
              }}
            >
              {totalResults}
            </Box>
            <Typography variant="subtitle1">
              {totalResults === 1 ? t('allTechniques.sheetFound') : t('allTechniques.sheetsFound')}
            </Typography>
          </Box>

          <Box sx={{ p: 2 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder={t('allTechniques.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              size="small"
              sx={{ mb: 2 }}
            />

            {paginatedData.map((tech) => (
              <MobileTechniqueCard
                key={tech.id}
                tech={tech}
                open={openRows[tech.id] || false}
                onToggle={handleToggleRow}
                navigate={navigate}
                t={t}
                translateFromDB={translateFromDB}
              />
            ))}
          </Box>

          {/* Mobile Pagination */}
          {rowsPerPage !== -1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              <Pagination
                color="primary"
                count={Math.ceil(totalResults / rowsPerPage)}
                page={page + 1}
                onChange={handleChangeMobilePage}
              />
            </Box>
          )}
        </>
      ) : (
        <>
          {/* Desktop View */}
          <Grid container spacing={2} marginBottom={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder={t('allTechniques.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>

          <Box
            sx={{
              mb: 3,
              display: 'flex',
              alignItems: 'center',
              p: 2,
              bgcolor: theme.palette.primary.main,
              color: 'white',
              borderRadius: 1,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}
          >
            <Box
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                borderRadius: '50%',
                width: 48,
                height: 48,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 2,
                fontWeight: 'bold',
                fontSize: '1.2rem',
              }}
            >
              {totalResults}
            </Box>
            <Typography variant="h6">
              {totalResults === 1 ? t('allTechniques.sheetFound') : t('allTechniques.sheetsFound')}
            </Typography>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell align="center">{t('allTechniques.creator')}</TableCell>
                  <TableCell align="center">{t('common.date')}</TableCell>
                  <TableCell>{t('allTechniques.player')}</TableCell>
                  <TableCell align="center">{t('allTechniques.position')}</TableCell>
                  <TableCell align="center">{t('allTechniques.age')}</TableCell>
                  <TableCell align="center">{t('allTechniques.nationality')}</TableCell>
                  <TableCell align="center">{t('allTechniques.team')}</TableCell>
                  <TableCell align="center">{t('allTechniques.groups')}</TableCell>
                  <TableCell align="center">{t('allTechniques.height')}</TableCell>
                  <TableCell align="center">{t('allTechniques.weight')}</TableCell>
                  <TableCell align="center">{t('common.details')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.map((row) => {
                  const decimalAge = calculateAge(row.player_date_naissance, row.fecha);
                  const birthYear = row.player_date_naissance ? new Date(row.player_date_naissance).getFullYear() : null;
                  const shortNat = getShortNationality(row.player_nationalite);

                  return (
                    <React.Fragment key={row.id}>
                      <TableRow
                        hover
                        sx={{
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                          }
                        }}
                        onClick={(e) => {
                          // Ne pas toggler si on clique sur un lien, bouton ou action
                          if (
                            e.target.closest('button') ||
                            e.target.closest('[role="button"]') ||
                            e.target.closest('[data-player-link]')
                          ) {
                            return;
                          }
                          handleToggleRow(row.id);
                        }}
                      >
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Tooltip
                              arrow
                              componentsProps={{
                                tooltip: {
                                  sx: {
                                    bgcolor: 'white',
                                    '& .MuiTooltip-arrow': { color: 'white' },
                                    boxShadow: '0px 2px 8px rgba(0,0,0,0.15)',
                                  },
                                },
                              }}
                              title={
                                <Box sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                                  <Avatar
                                    src={getAvatarUrl(row.usuario_id)}
                                    sx={{ width: 50, height: 50 }}
                                  />
                                  <Box>
                                    <Typography
                                      variant="body1"
                                      sx={{ fontWeight: 'bold', color: 'black' }}
                                    >
                                      {row.usuario_nombre}
                                    </Typography>
                                  </Box>
                                </Box>
                              }
                            >
                              <Avatar
                                sx={{
                                  bgcolor: getAvatarColor(row.usuario_nom + row.usuario_prenom),
                                }}
                              >
                                {getInitials(row.usuario_prenom, row.usuario_nom)}
                              </Avatar>
                            </Tooltip>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          {new Date(row.fecha).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar
                              src={getPlayerAvatarUrl(row.player_id)}
                              alt={row.player_nom}
                              sx={{ mr: 1, cursor: 'pointer' }}
                              data-player-link
                              onClick={(e) => {
                                e.stopPropagation();
                                navigateToPlayer(row.player_id);
                              }}
                            />
                            <Typography
                              variant="body2"
                              sx={{
                                cursor: 'pointer',
                                '&:hover': { textDecoration: 'underline' }
                              }}
                              data-player-link
                              onClick={(e) => {
                                e.stopPropagation();
                                navigateToPlayer(row.player_id);
                              }}
                            >
                              {row.player_nom}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">{translateFromDB(row.player_poste) || '-'}</TableCell>
                        <TableCell align="center">
                          {birthYear && decimalAge ? `${birthYear} (${decimalAge})` : '-'}
                        </TableCell>
                        <TableCell align="center">{shortNat || '-'}</TableCell>
                        <TableCell align="center">
                          <Box>
                            {/* Si on a des données dans competitions_detail, les afficher en priorité */}
                            {row.player_competitions_detail && row.player_competitions_detail.length > 0 ? (
                              <>
                                {/* Grouper par équipe */}
                                {[...new Set(row.player_competitions_detail.map(c => c.equipe_nom))].map((equipe, idx) => {
                                  // Récupérer l'ID de l'équipe et les compétitions associées
                                  const equipeData = row.player_competitions_detail.find(c => c.equipe_nom === equipe);
                                  const competitions = row.player_competitions_detail
                                    .filter(c => c.equipe_nom === equipe)
                                    .filter((value, index, self) =>
                                      index === self.findIndex((c) => c.competition_id === value.competition_id)
                                    );
                                  const mediaUrl = getMediaBaseUrl();
                                  const timestamp = Date.now();

                                  return (
                                    <Box key={idx} sx={{ mb: 0.5 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                        {/* Logo de l'équipe */}
                                        <Box
                                          component="img"
                                          src={`${mediaUrl}/equipes/${equipeData.equipe_id}.jpg?t=${timestamp}`}
                                          alt={equipe}
                                          sx={{
                                            width: 16,
                                            height: 16,
                                            objectFit: 'contain',
                                            borderRadius: '2px'
                                          }}
                                          onError={(e) => {
                                            e.target.style.display = 'none';
                                          }}
                                        />
                                        <div style={{ fontWeight: 'bold', fontSize: '12px' }}>{equipe}</div>
                                      </Box>
                                      {/* Logos et noms des compétitions */}
                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.3, flexWrap: 'wrap', mt: 0.3 }}>
                                        {competitions.map((comp, compIdx) => (
                                          <Box key={compIdx} sx={{ display: 'flex', alignItems: 'center', gap: 0.2 }}>
                                            <Box
                                              component="img"
                                              src={`${mediaUrl}/competitions/${comp.competition_id}.jpg?t=${timestamp}`}
                                              alt={comp.competition_nom}
                                              sx={{
                                                width: 14,
                                                height: 14,
                                                objectFit: 'contain',
                                                borderRadius: '2px'
                                              }}
                                              onError={(e) => {
                                                e.target.style.display = 'none';
                                              }}
                                            />
                                            <span style={{ fontSize: '10px' }}>{comp.competition_nom}</span>
                                          </Box>
                                        ))}
                                      </Box>
                                    </Box>
                                  );
                                })}
                              </>
                            ) : (
                              // Sinon afficher l'ancien système (team et optionnellement les ligues)
                              <Box>
                                {row.player_team ? (
                                  <div style={{ fontSize: '12px' }}>{row.player_team}</div>
                                ) : (
                                  <div style={{ fontSize: '12px' }}>-</div>
                                )}
                                {(row.player_league_1 || row.player_league_2) && (
                                  <div style={{ fontSize: '10px', color: '#666' }}>
                                    {[row.player_league_1, row.player_league_2].filter(Boolean).join(' - ')}
                                  </div>
                                )}
                              </Box>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          {row.player_groupes ? (
                            <Stack direction="row" spacing={0.5} justifyContent="center" flexWrap="wrap">
                              {parseGroups(row.player_groupes).map((group) => (
                                <Chip
                                  key={group.id}
                                  label={group.libelle}
                                  size="small"
                                  sx={{
                                    backgroundColor: group.code_couleur,
                                    color: group.code_couleur === '#FFFFFF' ? 'text.primary' : 'white',
                                    fontWeight: 500,
                                    border: group.code_couleur === '#FFFFFF' ? '1px solid rgba(0,0,0,0.23)' : 'none',
                                  }}
                                />
                              ))}
                            </Stack>
                          ) : '-'}
                        </TableCell>
                        <TableCell align="center">
                          {row.altura_sin_zapatos ? `${row.altura_sin_zapatos} cm` : '-'}
                        </TableCell>
                        <TableCell align="center">
                          {row.peso_kg ? `${row.peso_kg} kg` : '-'}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleRow(row.id);
                            }}
                          >
                            {openRows[row.id] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                          </IconButton>
                        </TableCell>
                      </TableRow>

                      {/* Détails en Collapse */}
                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={11}>
                          <Collapse in={openRows[row.id]} timeout="auto" unmountOnExit>
                            <Box margin={2}>
                              {renderFichaContent(row)}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>

            {/* Desktop Pagination */}
            <TablePagination
              rowsPerPageOptions={[25, 50, 100, { label: t('common.all'), value: -1 }]}
              component="div"
              count={totalResults}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage={t('allTechniques.sheetsPerPage')}
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} ${t('allTechniques.of')} ${count}`}
            />
          </TableContainer>
        </>
      )}
    </Box>
  );
};

export default AllTechniquesTable;